import { WALL_EVENT_LABELS, WALL_MODE_LABELS, type WallEvent } from '@raro/domain';
import { cx, Icon, MachineWallBrand } from '@raro/ui';
import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { API_URL } from '../lib/api';
import type { SerendipityCard, WallEventRow, WallScreenData } from '../lib/types';

/**
 * machine.wall — colagem viva. Duas TVs, uma sessão, sem login (token na URL).
 * /wall?session=<id>&token=<t>&screen=left|right|single
 *
 * A tela não precisa ser totalmente legível. Precisa provocar.
 */
export function WallScreenPage() {
  const [params] = useSearchParams();
  const session = params.get('session') ?? '';
  const token = params.get('token') ?? '';
  const screen = (params.get('screen') as 'left' | 'right' | 'single' | null) ?? 'single';
  const [data, setData] = useState<WallScreenData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [now, setNow] = useState(new Date());

  const load = async () => {
    try {
      const res = await fetch(`${API_URL}/wall/session/${session}?token=${encodeURIComponent(token)}`);
      if (!res.ok) throw new Error(`${res.status}`);
      setData((await res.json()) as WallScreenData);
      setError(null);
    } catch (e) {
      setError(`sem sessão (${(e as Error).message})`);
    }
  };

  useEffect(() => {
    if (!session) return;
    void load();
    const es = new EventSource(`${API_URL}/wall/session/${session}/stream?token=${encodeURIComponent(token)}`);
    es.addEventListener('event', (e) => {
      const ev = JSON.parse((e as MessageEvent).data) as WallEventRow;
      setData((d) => (d ? { ...d, events: [ev, ...d.events].slice(0, 40) } : d));
      setTick((t) => t + 1);
    });
    es.addEventListener('session', () => void load());
    es.addEventListener('serendipity', (e) => {
      const card = JSON.parse((e as MessageEvent).data) as SerendipityCard;
      setData((d) => (d ? { ...d, serendipity: [card, ...d.serendipity].slice(0, 12) } : d));
    });
    es.onerror = () => undefined;
    const refresh = setInterval(() => void load(), 5 * 60_000);
    const rot = setInterval(() => setTick((t) => t + 1), 18_000);
    const clock = setInterval(() => setNow(new Date()), 15_000);
    return () => {
      es.close();
      clearInterval(refresh);
      clearInterval(rot);
      clearInterval(clock);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, token]);

  if (!session) return <div className="rr-wallscreen"><div className="rr-wall-empty">abra /wall?session=…&token=…&screen=left|right</div></div>;
  if (error) return <div className="rr-wallscreen"><div className="rr-wall-empty">{error}</div></div>;
  if (!data) return <div className="rr-wallscreen"><div className="rr-wall-empty">…</div></div>;

  return (
    <div className="rr-wallscreen">
      <div className="rr-wallscreen__brand"><MachineWallBrand /></div>
      <div className="rr-wallscreen__clock">
        <span className="rr-wallscreen__time">{now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
        <span className="rr-wallscreen__date">{now.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }).replace('.', '')}<br />{now.toLocaleDateString('pt-BR', { weekday: 'long' })}</span>
      </div>
      <div className="rr-wallscreen__mode">{WALL_MODE_LABELS[data.mode]}{screen !== 'single' ? ` · ${screen}` : ''}</div>
      {data.mode === 'ambient' && <Ambient data={data} screen={screen} tick={tick} />}
      {data.mode === 'focus' && <Focus data={data} screen={screen} tick={tick} />}
      {data.mode === 'project' && <ProjectMode data={data} screen={screen} tick={tick} />}
      {data.mode === 'serendipity' && <SerendipityMode data={data} screen={screen} tick={tick} />}
      <Doodles seed={tick * 7 + (screen === 'right' ? 101 : 0)} />
      <Ticker events={data.events} />
    </div>
  );
}

// ───────────────────────── util ─────────────────────────

function rng(seed: number) {
  let a = seed >>> 0 || 1;
  return () => {
    a += 0x6d2b79f5;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function rotate<T>(arr: T[], by: number): T[] {
  if (arr.length === 0) return arr;
  const k = ((by % arr.length) + arr.length) % arr.length;
  return [...arr.slice(k), ...arr.slice(0, k)];
}
const PROVOCATIONS = ['O que estamos deixando de ver?', 'O que estamos assumindo?', 'E se fosse o contrário?', 'O que ninguém nesse mercado faria?', 'Qual é a primeira resposta óbvia? Agora descarte-a.', 'E se removêssemos isso?', 'Como outra indústria resolveria?'];
const SLOGANS = [['A máquina', 'encontra padrões.'], ['A Raro', 'procura exceções.'], ['Tornar visível', 'o que é visível.'], ['Mais contexto.', 'Mais pergunta.'], ['Mais verdade.', 'Menos óbvio.']];

interface Tile {
  key: string;
  w: number;
  h: number;
  kind?: string;
  rot?: number;
  z?: number;
  node: ReactNode;
}

const GRID_CELLS = 12 * 8;

/** Mantém só o que cabe na grade (12 × 8). O que sobra fica para a próxima rotação. */
function fit(tiles: Tile[]): Tile[] {
  let area = 0;
  const kept: Tile[] = [];
  for (const t of tiles) {
    const a = t.w * t.h;
    if (area + a > GRID_CELLS - 4) continue;
    kept.push(t);
    area += a;
  }
  return kept;
}

function Tiles({ tiles, seed }: { tiles: Tile[]; seed: number }) {
  const r = rng(seed);
  return (
    <div className="rr-collage">
      {fit(tiles).map((t) => {
        const rot = t.rot ?? (r() - 0.5) * 2.2;
        const dx = (r() - 0.5) * 0.6;
        const dy = (r() - 0.5) * 0.6;
        const style: CSSProperties = { gridColumn: `span ${t.w}`, gridRow: `span ${t.h}`, transform: `rotate(${rot}deg) translate(${dx}vw, ${dy}vw)`, zIndex: t.z ?? 1 };
        return <div key={t.key} className={cx('rr-tile', t.kind && `rr-tile--${t.kind}`)} style={style}>{t.node}</div>;
      })}
    </div>
  );
}

const Kicker = ({ a, b, on }: { a: ReactNode; b?: ReactNode; on?: boolean }) => <div className={cx('rr-tile__kicker', on && 'rr-tile__kicker--on')}><span>{a}</span>{b && <span>{b}</span>}</div>;
const Foot = ({ a, b }: { a: ReactNode; b?: ReactNode }) => <div className="rr-tile__foot"><span>{a}</span>{b && <span>{b}</span>}</div>;
const Bars = ({ seed, n = 9 }: { seed: number; n?: number }) => { const r = rng(seed); return <div className="rr-tile__bars">{Array.from({ length: n }).map((_, i) => <i key={i} style={{ height: `${20 + r() * 80}%` }} />)}</div>; };
const Wave = ({ seed }: { seed: number }) => { const r = rng(seed); return <div className="rr-tile__wave"><span className="rr-tile__play"><Icon name="play" size={14} filled /></span>{Array.from({ length: 36 }).map((_, i) => <i key={i} style={{ height: `${15 + r() * 85}%` }} />)}</div>; };
const dirArrow = (d: number) => (d > 0 ? '↗' : d < 0 ? '↘' : '→');

// ───────────────────────── AMBIENT ─────────────────────────

function Ambient({ data, screen, tick }: { data: WallScreenData; screen: string; tick: number }) {
  const side = screen === 'right' ? 1 : 0;
  const seed = tick * 13 + side * 97;
  const r = rng(seed);
  const shift = tick + side * 3;
  const images = rotate(data.images, shift * 2);
  const quotes = rotate(data.quotes, shift);
  const seenTitles = new Set<string>();
  const events = rotate(data.events.filter((e) => (seenTitles.has(e.title) ? false : (seenTitles.add(e.title), true))), shift);
  const signals = rotate(data.signals, shift);
  const questions = rotate(data.openQuestions, shift);
  const cards = rotate(data.serendipity, shift);
  const hyps = data.hypotheses;
  const projects = rotate(data.activeProjects, side);
  const effects = rotate(data.effects, shift);
  const experiments = rotate(data.experiments, shift);
  const learnings = rotate(data.learnings, shift);
  const campo = rotate(data.campo, shift);
  const m = data.memory;

  const tiles: Tile[] = [];
  const push = (t: Tile) => tiles.push(t);

  // 1) cabeça da tela: provocação manuscrita ou slogan — nunca nas duas telas ao mesmo tempo
  const headline = (tick + side) % 2 === 0;
  if (headline) {
    const p = PROVOCATIONS[(tick + side) % PROVOCATIONS.length]!;
    push({ key: 'prov', w: 4, h: 3, kind: 'bare', rot: -1.5, z: 5, node: <div className="rr-tile__hand">{p}</div> });
  } else {
    const sl = SLOGANS[(tick + side) % SLOGANS.length]!;
    push({ key: 'slogan', w: 5, h: 3, kind: 'bare', rot: 0, z: 5, node: <div className="rr-tile__slogan">{sl[0]}<br />{sl[1]}</div> });
  }
  // 2) fotos: 3–5, tamanhos variados, metade p&b
  images.slice(0, 4).forEach((img, i) => {
    const big = i === 0;
    push({ key: `img-${img.id}`, w: big ? 3 : 2, h: big ? 4 : i % 2 ? 3 : 2, kind: cx('photo', r() > 0.5 && 'bw'), rot: (r() - 0.5) * 3, node: <><img src={`${API_URL}${img.url}`} alt="" /><span className="rr-tile__cap">{img.organization}{img.project ? ` / ${img.project}` : ''}</span></> });
  });
  // 3) voz
  if (quotes[0]) push({ key: 'voz', w: 3, h: 3, node: <><Kicker a={<><b>{quotes[0].organization}</b> / voz</>} /><div className="rr-tile__quote">“{quotes[0].text}”</div><Foot a={quotes[0].project ?? 'entrevista'} b={<Wave seed={seed + 3} />} /></> });
  // 4) sinal com tendência + mini barras
  if (signals[0]) push({ key: 'sig', w: 3, h: 2, node: <><Kicker a={<><b>{signals[0].organization ?? 'raro'}</b> / {signals[0].kind}</>} on /><div className="rr-tile__text" style={{ fontSize: 'clamp(13px, 1.05vw, 22px)', color: 'var(--rr-text)' }}>{signals[0].text}</div><Foot a={<span style={{ color: signals[0].direction > 0 ? 'var(--rr-up)' : signals[0].direction < 0 ? 'var(--rr-down)' : 'var(--rr-accent)' }}>{dirArrow(signals[0].direction)} tendência</span>} /><Bars seed={seed + 5} /></> });
  // 5) post-it manuscrito com um evento/aprendizado/segundo sinal
  const note = learnings[0]?.text ?? signals[1]?.text ?? events[0]?.title;
  if (note) push({ key: 'note', w: 2, h: 2, kind: cx('paper', r() > 0.7 && 'yellow'), rot: (r() - 0.5) * 7, z: 6, node: <div>{note.length > 110 ? `${note.slice(0, 107)}…` : note}</div> });
  // 6) hipóteses de um projeto
  const pj = projects[0];
  const hl = pj ? hyps.filter((h) => h.projectId === pj.id).slice(0, 4) : hyps.slice(0, 4);
  if (hl.length) push({ key: 'hyp', w: 3, h: 2, node: <><Kicker a={<>{pj ? <b>{pj.organization}</b> : 'o raro'} / hipóteses</>} /><ol className="rr-tile__list">{hl.map((h, i) => <li key={h.id}><span>{String(i + 1).padStart(2, '0')}</span><span>{h.title}</span></li>)}</ol></> });
  // 7) memória raro
  push({ key: 'mem', w: 2, h: 3, node: <><Kicker a="memória raro" /><div className="rr-tile__stats"><div className="rr-tile__stat"><b>{m.projects}</b><span>projetos</span></div><div className="rr-tile__stat"><b>{m.evidence}</b><span>evidências</span></div><div className="rr-tile__stat"><b>{m.hypotheses}</b><span>hipóteses</span></div><div className="rr-tile__stat"><b>{m.sources}</b><span>fontes</span></div></div></> });
  // 8) serendipidade
  if (cards[0]) {
    const c = cards[0];
    const ia = images[1];
    const ib = images[2];
    push({ key: 'ser', w: 3, h: 3, node: <><Kicker a={<><b>serendipidade</b></>} b="existe um padrão aqui?" /><div className="rr-tile__pair">{ia ? <div><img src={`${API_URL}${ia.url}`} alt="" /><small>{c.aLabel}</small></div> : <small>{c.aLabel}</small>}<span className="rr-tile__x">×</span>{ib ? <div><img src={`${API_URL}${ib.url}`} alt="" /><small>{c.bLabel}</small></div> : <small>{c.bLabel}</small>}</div><div className="rr-tile__hand rr-tile__hand--sm">{c.pattern.length > 90 ? `${c.pattern.slice(0, 87)}…` : c.pattern}</div></> });
  }
  // 9) campo / hoje
  if (campo.length) push({ key: 'campo', w: 3, h: 2, node: <><Kicker a={<><b>campo</b> / hoje</>} on /><ul className="rr-tile__list rr-tile__list--icons">{campo.slice(0, 3).map((c) => <li key={c.id}><span><Icon name={c.type === 'image' ? 'image' : c.type === 'audio' || c.type === 'video' ? 'video' : 'pencil'} size={14} /></span><span>{c.title.length > 48 ? `${c.title.slice(0, 45)}…` : c.title}</span></li>)}</ul></> });
  // 10) efeito
  if (effects[0]) {
    const e = effects[0];
    const b = e.before != null ? Number(e.before) : null;
    const a = e.after != null ? Number(e.after) : null;
    const pct = b !== null && a !== null && b ? Math.round(((a - b) / Math.abs(b)) * 100) : null;
    push({ key: 'eff', w: 2, h: 3, node: <><Kicker a={<><b>{e.organization ?? 'efeito'}</b> / efeito</>} /><div className={cx('rr-tile__delta', e.direction > 0 && 'rr-tile__delta--up', e.direction < 0 && 'rr-tile__delta--down')}>{pct !== null ? `${pct > 0 ? '+' : ''}${pct}%` : dirArrow(e.direction)}</div><div className="rr-tile__text">{e.metric ?? e.text}</div></> });
  }
  // 11) provar
  if (experiments[0]) push({ key: 'exp', w: 2, h: 2, node: <><Kicker a={<><b>{experiments[0].organization ?? 'provar'}</b> / provar</>} /><div className="rr-tile__text" style={{ color: 'var(--rr-text)', fontSize: 'clamp(12px, 1vw, 20px)' }}>{experiments[0].title}</div><Foot a={experiments[0].outcome ?? experiments[0].status} /></> });
  // 12) pergunta aberta
  if (questions[0]) push({ key: 'q', w: 3, h: 2, node: <><Kicker a="pergunta aberta" /><div className="rr-tile__big">{questions[0].text}</div></> });
  // 13) eventos recentes
  events.slice(0, 2).forEach((e, i) => push({ key: `ev-${e.id}-${i}`, w: 2, h: 2, node: <><Kicker a={<b>{WALL_EVENT_LABELS[e.kind as WallEvent] ?? e.kind}</b>} b={e.payload.organization ?? ''} /><div className="rr-tile__text" style={{ color: 'var(--rr-text)', fontSize: 'clamp(12px, 1vw, 20px)' }}>{e.title.length > 120 ? `${e.title.slice(0, 117)}…` : e.title}</div></> }));
  // preenchimento: segunda voz, segundo sinal, mais eventos, aprendizado, fotos
  if (quotes[1]) push({ key: 'voz2', w: 3, h: 2, node: <><Kicker a={<><b>{quotes[1].organization}</b> / voz</>} /><div className="rr-tile__quote" style={{ fontSize: 'clamp(14px, 1.3vw, 30px)' }}>“{quotes[1].text}”</div></> });
  if (signals[1]) push({ key: 'sig2', w: 2, h: 2, node: <><Kicker a={<><b>{signals[1].organization ?? 'raro'}</b> / sinal</>} /><div className="rr-tile__text" style={{ color: 'var(--rr-text)' }}>{signals[1].text}</div></> });
  events.slice(2, 5).forEach((e, i) => push({ key: `ev2-${e.id}-${i}`, w: 2, h: 2, node: <><Kicker a={<b>{WALL_EVENT_LABELS[e.kind as WallEvent] ?? e.kind}</b>} b={e.payload.organization ?? ''} /><div className="rr-tile__text" style={{ color: 'var(--rr-text)' }}>{e.title}</div></> }));
  if (learnings[1]) push({ key: 'lrn2', w: 3, h: 2, node: <><Kicker a={<><b>aprendizado</b></>} /><div className="rr-tile__quote" style={{ fontSize: 'clamp(14px, 1.3vw, 30px)' }}>{learnings[1].text}</div></> });
  images.slice(4, 8).forEach((img, i) => push({ key: `img2-${img.id}-${i}`, w: 2, h: 2, kind: cx('photo', i % 2 === 0 && 'bw'), node: <img src={`${API_URL}${img.url}`} alt="" /> }));
  if (experiments[1]) push({ key: 'exp2', w: 2, h: 2, node: <><Kicker a={<><b>provar</b></>} /><div className="rr-tile__text" style={{ color: 'var(--rr-text)' }}>{experiments[1].title}</div><Foot a={experiments[1].outcome ?? experiments[1].status} /></> });
  if (questions[1]) push({ key: 'q2', w: 2, h: 2, node: <><Kicker a="pergunta" /><div className="rr-tile__text" style={{ color: 'var(--rr-text)', fontSize: 'clamp(12px, 1vw, 20px)' }}>{questions[1].text}</div></> });
  push({ key: 'bars2', w: 2, h: 2, node: <><Kicker a={<><b>radar</b> / mercado</>} /><div className="rr-tile__text">movimentos dos últimos dias</div><Bars seed={seed + 21} n={12} /></> });

  // embaralha de leve a ordem (mantendo a cabeça primeiro)
  const [head, ...rest] = tiles;
  const shuffled = rest.map((t) => ({ t, k: r() })).sort((a, b) => a.k - b.k).map((x) => x.t);
  return <Tiles key={seed} tiles={[head!, ...shuffled]} seed={seed} />;
}

// ───────────────────────── FOCUS ─────────────────────────

function Focus({ data, screen, tick }: { data: WallScreenData; screen: string; tick: number }) {
  const f = data.focus;
  if (!f) return <div className="rr-wall-empty">nada em foco.</div>;
  if (f.kind === 'serendipity' && f.card) return <SerendipityMode data={{ ...data, serendipity: [f.card] }} screen={screen} tick={0} />;
  const isEvidence = f.entity?.type === 'evidence' || f.entity?.type === 'learning';
  const title = f.entity ? (isEvidence ? `“${f.entity.text}”` : f.entity.title) : (f.title ?? f.text ?? '');
  const text = f.entity ? (f.entity.type === 'hypothesis' ? f.entity.text : '') : f.title ? (f.text ?? '') : '';
  const images = rotate(data.images, tick + (screen === 'right' ? 1 : 0));
  const tiles: Tile[] = [];
  if (screen !== 'right') {
    tiles.push({ key: 'ft', w: 7, h: 6, kind: 'bare', rot: 0, z: 5, node: <><Kicker a={<b>{f.entity?.type ?? 'em foco'}</b>} /><div className={cx(isEvidence ? 'rr-tile__quote' : 'rr-tile__slogan')} style={{ fontSize: isEvidence ? 'clamp(30px, 3.6vw, 90px)' : 'clamp(32px, 3.8vw, 96px)' }}>{title}</div>{screen === 'single' && text && <div className="rr-tile__text" style={{ fontSize: 'clamp(16px, 1.4vw, 32px)', maxWidth: '44ch' }}>{text}</div>}</> });
    images.slice(0, 2).forEach((img, i) => tiles.push({ key: `fi-${i}`, w: i === 0 ? 5 : 3, h: i === 0 ? 5 : 3, kind: cx('photo', i === 1 && 'bw'), node: <img src={`${API_URL}${img.url}`} alt="" /> }));
  } else {
    tiles.push({ key: 'ctx', w: 7, h: 4, kind: 'bare', rot: 0, z: 5, node: <><Kicker a={<b>contexto</b>} /><div className="rr-tile__big" style={{ fontSize: 'clamp(22px, 2.4vw, 56px)', color: 'var(--rr-text-2)' }}>{text || title}</div></> });
    tiles.push({ key: 'fh', w: 5, h: 3, kind: 'bare', node: <div className="rr-tile__hand">{PROVOCATIONS[tick % PROVOCATIONS.length]}</div> });
    images.slice(0, 3).forEach((img, i) => tiles.push({ key: `fi-${i}`, w: i === 0 ? 4 : 3, h: 3, kind: cx('photo', i % 2 === 1 && 'bw'), node: <img src={`${API_URL}${img.url}`} alt="" /> }));
  }
  return <Tiles key={`f-${tick}-${screen}`} tiles={tiles} seed={tick + 500} />;
}

// ───────────────────────── PROJECT ─────────────────────────

function ProjectMode({ data, screen, tick }: { data: WallScreenData; screen: string; tick: number }) {
  const p = data.project;
  if (!p) return <div className="rr-wall-empty">escolha um projeto no controle.</div>;
  const side = screen === 'right' ? 1 : 0;
  const seed = tick * 7 + side * 31;
  const r = rng(seed);
  const items = p.items.filter((_, i) => (screen === 'left' ? i % 2 === 0 : screen === 'right' ? i % 2 === 1 : true));
  const images = rotate(data.images.filter((i) => i.project === p.name), tick);
  const hyps = data.hypotheses.filter((h) => h.projectId === p.id);
  const quotes = rotate(data.quotes.filter((q) => q.project === p.name), tick);
  const tiles: Tile[] = [];
  if (side === 0) {
    tiles.push({ key: 'title', w: 5, h: 3, kind: 'bare', rot: 0, z: 5, node: <><div className="rr-tile__slogan" style={{ fontSize: 'clamp(44px, 6vw, 150px)' }}>{p.organization.toUpperCase()}</div><Kicker a={<b>{p.name}</b>} /></> });
    if (p.currentQuestion) tiles.push({ key: 'q', w: 4, h: 3, kind: 'bare', rot: -1, z: 5, node: <div className="rr-tile__hand" style={{ fontSize: 'clamp(22px, 2.4vw, 56px)' }}>{p.currentQuestion}</div> });
  } else {
    tiles.push({ key: 'ctx', w: 3, h: 3, node: <><Kicker a={<b>contexto</b>} /><ul className="rr-tile__list">{['voz', 'parede', 'hipóteses', 'experimentos', 'resultados'].map((k, i) => <li key={k}><span>{String(i + 1).padStart(2, '0')}</span><span style={{ textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: '0.8em' }}>{k}</span></li>)}</ul></> });
    if (hyps.length) tiles.push({ key: 'hyp', w: 3, h: 2, node: <><Kicker a={<><b>o raro</b> / hipóteses</>} /><ol className="rr-tile__list">{hyps.slice(0, 4).map((h, i) => <li key={h.id}><span>{String(i + 1).padStart(2, '0')}</span><span>{h.title}</span></li>)}</ol></> });
  }
  images.slice(0, 3).forEach((img, i) => tiles.push({ key: `img-${img.id}-${i}`, w: i === 0 ? 4 : 2, h: i === 0 ? 4 : 2, kind: cx('photo', i === 2 && 'bw'), rot: (r() - 0.5) * 3, node: <img src={`${API_URL}${img.url}`} alt="" /> }));
  if (quotes[0]) tiles.push({ key: 'voz', w: 3, h: 2, node: <><Kicker a={<><b>{p.organization}</b> / voz</>} /><div className="rr-tile__quote" style={{ fontSize: 'clamp(16px, 1.5vw, 34px)' }}>“{quotes[0].text}”</div></> });
  items.slice(0, 8).forEach((it, i) => {
    const e = it.entity;
    const kind = it.kind === 'ref' ? it.refType : it.kind;
    if (it.kind === 'note') tiles.push({ key: it.id, w: 2, h: 2, kind: cx('paper', it.color === 'yellow' && 'yellow'), rot: it.rotation || (r() - 0.5) * 6, z: 4, node: <div>{it.text}</div> });
    else if (e?.type === 'evidence') tiles.push({ key: it.id, w: 3, h: 2, node: <><Kicker a="o que ouvimos" /><div className="rr-tile__quote" style={{ fontSize: 'clamp(15px, 1.4vw, 32px)' }}>“{e.text}”</div></> });
    else if (e?.type === 'hypothesis') tiles.push({ key: it.id, w: 3, h: 2, node: <><Kicker a={<b>hipótese</b>} /><div className="rr-tile__big" style={{ fontSize: 'clamp(16px, 1.5vw, 34px)' }}>{e.title}</div><div className="rr-tile__text">{e.text}</div></> });
    else if (e) tiles.push({ key: it.id, w: 2, h: 2, node: <><Kicker a={<b>{kind}</b>} /><div className="rr-tile__text" style={{ color: 'var(--rr-text)' }}>{e.title}</div></> });
    if (i === 3) tiles.push({ key: 'bars', w: 2, h: 2, node: <><Kicker a="sinais" /><Bars seed={seed + 9} n={11} /></> });
  });
  return <Tiles key={`p-${seed}`} tiles={tiles} seed={seed} />;
}

// ───────────────────────── SERENDIPITY ─────────────────────────

function SerendipityMode({ data, screen, tick }: { data: WallScreenData; screen: string; tick: number }) {
  const card = rotate(data.serendipity, tick)[0];
  if (!card) return <div className="rr-wall-empty">nenhuma conexão inesperada ainda.</div>;
  const images = rotate(data.images, tick);
  const ia = images[0];
  const ib = images[1];
  const tiles: Tile[] = [];
  const side = screen === 'right' ? 'b' : screen === 'left' ? 'a' : 'ab';
  tiles.push({ key: 'hd', w: 12, h: 1, kind: 'bare', rot: 0, node: <div className="rr-tile__slogan" style={{ fontSize: 'clamp(28px, 3vw, 72px)' }}>SERENDIPIDADE</div> });
  if (side !== 'b') {
    if (ia) tiles.push({ key: 'ia', w: 4, h: 4, kind: 'photo', node: <><img src={`${API_URL}${ia.url}`} alt="" /><span className="rr-tile__cap">{card.aLabel}</span></> });
    tiles.push({ key: 'ta', w: side === 'ab' ? 3 : 7, h: 4, node: <><Kicker a={<b>{card.aLabel}</b>} /><div className="rr-tile__quote">“{card.aText.length > 220 ? `${card.aText.slice(0, 217)}…` : card.aText}”</div></> });
  }
  if (side === 'ab') tiles.push({ key: 'x', w: 1, h: 4, kind: 'bare', node: <div className="rr-tile__x" style={{ color: 'var(--rr-accent)', fontSize: 'clamp(40px, 5vw, 120px)', textAlign: 'center', marginTop: '30%' }}>×</div> });
  if (side !== 'a') {
    if (ib) tiles.push({ key: 'ib', w: 4, h: 4, kind: 'photo bw', node: <><img src={`${API_URL}${ib.url}`} alt="" /><span className="rr-tile__cap">{card.bLabel}</span></> });
    tiles.push({ key: 'tb', w: side === 'ab' ? 3 : 7, h: 4, node: <><Kicker a={<b>{card.bLabel}</b>} /><div className="rr-tile__quote">“{card.bText.length > 220 ? `${card.bText.slice(0, 217)}…` : card.bText}”</div></> });
  }
  tiles.push({ key: 'pat', w: side === 'ab' ? 7 : 12, h: 3, kind: 'bare', node: <><div className="rr-tile__hand" style={{ fontSize: 'clamp(24px, 2.6vw, 64px)' }}>{card.pattern}</div><div className="rr-tile__text" style={{ fontSize: 'clamp(14px, 1.2vw, 26px)', maxWidth: '60ch' }}>{card.question}</div></> });
  if (side === 'ab') tiles.push({ key: 'why', w: 5, h: 3, node: <><Kicker a="por que importa" /><div className="rr-tile__text" style={{ fontSize: 'clamp(13px, 1.1vw, 24px)' }}>{card.why}</div><Foot a={`relevância ${Math.round((card.relevance ?? 0) * 100)}%`} b={card.method ?? ''} /></> });
  return <Tiles key={`s-${card.id}-${screen}`} tiles={tiles} seed={tick + 900} />;
}

// ───────────────────────── rabiscos ─────────────────────────

function Doodles({ seed }: { seed: number }) {
  const r = rng(seed + 1);
  const items = useMemo(() => Array.from({ length: 5 }).map((_, i) => ({ i, x: 5 + r() * 85, y: 10 + r() * 78, s: 4 + r() * 6, k: Math.floor(r() * 4), y2: r() > 0.7 })), [seed]); // eslint-disable-line react-hooks/exhaustive-deps
  const paths = [
    'M2 20 C 10 2, 30 2, 38 20 S 60 38, 70 20',
    'M5 30 L 40 8 L 36 20 M40 8 L 28 10',
    'M10 10 c 10 -14, 36 -10, 30 6 c -6 16 -34 12 -30 -4 c 4 -14 26 -10 24 2',
    'M4 24 q 10 -20 20 0 t 20 0 t 20 0',
  ];
  return (
    <>
      {items.map((d) => (
        <svg key={d.i} className={cx('rr-doodle', d.y2 && 'rr-doodle--yellow')} style={{ left: `${d.x}vw`, top: `${d.y}vh`, width: `${d.s}vw`, transform: `rotate(${(r() - 0.5) * 60}deg)` }} viewBox="0 0 80 40" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d={paths[d.k]} />
        </svg>
      ))}
    </>
  );
}

function Ticker({ events }: { events: WallEventRow[] }) {
  const items = useMemo(() => events.slice(0, 8), [events]);
  if (items.length === 0) return null;
  return <div className="rr-wall-ticker">{items.map((e) => <span key={e.id}><b>{WALL_EVENT_LABELS[e.kind as WallEvent] ?? e.kind}</b> {e.title.slice(0, 70)}{e.title.length > 70 ? '…' : ''}</span>)}</div>;
}
